/**
 * ATO & SRO Panel — data table of authority entries with a Sheet detail panel.
 *
 * Reads canonical entries from the `atoSroEntries` table.
 * Follows the same UI patterns as AssetListPanel / AssetPanel.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import ReactMarkdown from "react-markdown";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Separator } from "@/ui/primitives/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/ui/composed/overlays/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Loader2,
  Landmark,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  FileText,
  Pencil,
  Plus,
  Trash2,
  X,
  Info,
  CheckCircle2,
  Circle,
} from "lucide-react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/ui/composed/overlays/tooltip";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState,
  type ColumnDef,
} from "@tanstack/react-table";
import { cn } from "@/utils/misc";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import {
  ATO_SRO_FIELD_FORMATTERS,
  ATO_SRO_FIELD_LABELS,
} from "./historyFormatters";
import { SlideOverPanel } from "../shared-panels/SlideOverPanel";
import { LinkedRecordsSection } from "../shared-panels/LinkedRecordsSection";
import { SuggestedLinksSection } from "../shared-panels/SuggestedLinksSection";
import { LinkIndicator } from "../shared-panels/LinkIndicator";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../shared-panels/ConfirmEditDialog";
import {
  useLinkCounts,
  useLinkedRecordStack,
  type RecordType,
} from "../shared-panels/linkShared";
import { SecondaryDetailPanel } from "../shared-panels/SecondaryDetailPanel";
import { DocumentDetail } from "../../documents/DocumentDetail";
import type { AtoSroEntryUI } from "./types";
import { LedgerDetailCard } from "./LedgerDetailCard";
import {
  useFlagDismissals,
  flagGlowClass,
  FlagDismissButton,
  FlagDismissEditor,
  type UseFlagDismissalsResult,
} from "../flags";

/** Detail text feeding a flag's stable key — joins the bullet list. */
function atoFlagDetail(details: string[]): string {
  return details.join("|");
}

/** Small info icon with an explanatory tooltip (keeps the UI uncluttered). */
function InfoHint({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground/50 transition-colors hover:text-muted-foreground"
            aria-label="More info"
          >
            <Info className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs font-normal leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FLAG_TYPE_LABELS: Record<string, string> = {
  non_matching_balances: "Non-matching balances",
  round_payments: "Round payments",
  payment_plan: "Payment plan",
  warning_letters: "Warning letters",
  dpn_issued: "DPN issued",
  garnishee_notice: "Garnishee notice",
  late_lodgement: "Late lodgement",
  other: "Other",
};

type FlagSeverity = "critical" | "warning" | "info";

const FLAG_SEVERITY: Record<string, FlagSeverity> = {
  dpn_issued: "critical",
  garnishee_notice: "critical",
  non_matching_balances: "warning",
  round_payments: "warning",
  payment_plan: "info",
  warning_letters: "warning",
  late_lodgement: "warning",
  other: "info",
};

function getAuthorityShortLabel(authority: string): string {
  if (authority === "ato") return "ATO";
  if (authority.startsWith("sro_")) return "SRO";
  return "Other";
}

function getAuthorityBadgeColor(authority: string) {
  if (authority === "ato") return "blue" as const;
  if (authority.startsWith("sro_")) return "violet" as const;
  return "gray" as const;
}

function fmt(v: number | null | undefined): string {
  if (v == null) return "N/A";
  return "$" + Number(v).toLocaleString("en-AU", { minimumFractionDigits: 0 });
}

/**
 * A balance for display: positive = owing (DR), negative = the account is in
 * credit — shown explicitly as "CR" rather than a bare minus, so arrears and
 * credit can never be confused (MOR-807).
 */
function fmtBalance(v: number | null | undefined): string {
  if (v == null) return "N/A";
  if (v < 0) return fmt(Math.abs(v)) + " CR";
  return fmt(v);
}

/**
 * The authoritative running balance for an authority: the rebuilt ATO RBA
 * ledger closing (full transaction history) when available, otherwise the
 * statement / disclosure-derived running balance (MOR-587).
 */
function getEffectiveBalance(e: AtoSroEntryUI): number | undefined {
  return (
    e.selectedBalance?.amount ??
    e.ledger?.closingBalance ??
    e.runningBalance?.amount
  );
}

const BALANCE_SOURCE_LABELS: Record<string, string> = {
  rba_ledger: "ATO RBA (rebuilt)",
  proof_of_debt: "Proof of Debt",
  ato_statement: "ATO statement",
  director_gl: "Director GL",
  other: "Other",
};

function normaliseNotesForMarkdown(raw: string): string {
  return raw.replace(/\s*•\s*/g, "\n- ").trim();
}

// ---------------------------------------------------------------------------
// Column definitions (tanstack table)
// ---------------------------------------------------------------------------

function makeColumns(
  flagCtx: UseFlagDismissalsResult,
  linkCounts: ReturnType<typeof useLinkCounts>,
): ColumnDef<AtoSroEntryUI>[] {
  return [
    {
      accessorKey: "authority",
      header: "Authority",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Badge
            color={getAuthorityBadgeColor(row.original.authority)}
            variant="soft"
            size="sm"
            className="font-bold tracking-wide"
          >
            {getAuthorityShortLabel(row.original.authority)}
          </Badge>
          <LinkIndicator
            count={linkCounts.byLinkedEntity.get(row.original._id) ?? 0}
          />
        </div>
      ),
    },
    {
      id: "runningBalance",
      header: "Running Balance",
      accessorFn: (row) => getEffectiveBalance(row) ?? 0,
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1.5">
          {row.original.ledger && (
            <Badge variant="secondary" size="sm" className="text-[9px]">
              RBA
            </Badge>
          )}
          <span
            className={cn(
              "text-sm font-semibold tabular-nums",
              (getEffectiveBalance(row.original) ?? 0) < 0
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-rose-600 dark:text-rose-400",
            )}
          >
            {fmtBalance(getEffectiveBalance(row.original))}
          </span>
        </div>
      ),
    },
    {
      id: "companyBalance",
      header: "Company Balance",
      accessorFn: (row) => row.companyBalance?.amount ?? -1,
      cell: ({ row }) => (
        <span
          className={cn(
            "text-sm tabular-nums",
            row.original.companyBalance?.amount != null
              ? "text-foreground"
              : "text-muted-foreground/50",
          )}
        >
          {fmt(row.original.companyBalance?.amount)}
        </span>
      ),
    },
    {
      id: "flags",
      header: "Flags",
      cell: ({ row }) => {
        const flags = row.original.flags ?? [];
        if (flags.length === 0) return null;
        const entryId = row.original._id;
        return (
          <div className="flex flex-wrap gap-1">
            {flags.map((f) => {
              const severity = FLAG_SEVERITY[f.type] ?? "info";
              const color: "red" | "amber" | "sky" =
                severity === "critical"
                  ? "red"
                  : severity === "warning"
                    ? "amber"
                    : "sky";
              const detail = atoFlagDetail(f.details);
              const dismissed = flagCtx.isDismissed("atoSro", entryId, {
                type: f.type,
                detail,
              });
              const label = FLAG_TYPE_LABELS[f.type] ?? f.type;
              return (
                <Badge
                  key={f.type}
                  color={color}
                  variant="soft"
                  size="sm"
                  className={cn(
                    "whitespace-nowrap",
                    dismissed ? "line-through opacity-40" : flagGlowClass(true),
                  )}
                >
                  {label}
                </Badge>
              );
            })}
          </div>
        );
      },
    },
    {
      id: "chevron",
      header: "",
      cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground/40" />,
    },
  ];
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AtoSroPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
  compact = false,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
  /**
   * When `true`, hides the "Total Running Balance" header and the "Add
   * Account" button so the panel renders as table-only. Used by the
   * Overview (Plan) page.
   */
  compact?: boolean;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const flagCtx = useFlagDismissals(jobId);
  const linkCounts = useLinkCounts(jobId);

  // Stack of side-by-side linked-record panels so the user can chase a chain
  // of related records (ATO/SRO → Creditor → SI ...) without losing context.
  const linkedStack = useLinkedRecordStack(
    selectedEntryId ? { type: "ato_sro", id: selectedEntryId } : null,
  );
  const columns = useMemo(
    () => makeColumns(flagCtx, linkCounts),
    [flagCtx, linkCounts],
  );

  const { data: allEntries } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoSroEntriesForJob, {
      jobId,
    }),
  );

  const entries = useMemo<AtoSroEntryUI[]>(
    () => allEntries ?? [],
    [allEntries],
  );

  const selectedEntry = useMemo(
    () => entries.find((e) => e._id === selectedEntryId) ?? null,
    [entries, selectedEntryId],
  );

  const totalRunning = useMemo(
    () => entries.reduce((s, e) => s + (getEffectiveBalance(e) ?? 0), 0),
    [entries],
  );

  const table = useReactTable({
    data: entries,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // ---------------------------------------------------------------------------
  // Empty / loading states
  // ---------------------------------------------------------------------------

  if (entries.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            Extracting ATO/SRO data...
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <div className="rounded-full bg-muted p-3">
          <Landmark className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          No ATO/SRO accounts found.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Card */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Header — Total Running Balance + Add Account (hidden in compact mode) */}
        {!compact && (
          <div className="flex items-start justify-between px-5 pb-4 pt-5">
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Total Running Balance
              </p>
              <p className="text-2xl font-bold tabular-nums text-rose-600 dark:text-rose-400">
                {fmt(totalRunning)}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {entries.length} account{entries.length !== 1 ? "s" : ""}
              </p>
            </div>
            {!isViewer && (
              <Button
                size="sm"
                onClick={() => setShowAddForm(true)}
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Account
              </Button>
            )}
          </div>
        )}

        {/* Data table */}
        <div className="min-w-0 border-t border-border">
          <div className="max-h-[max(20rem,calc(100vh-460px))] overflow-auto">
            <Table className="w-full table-fixed">
              <colgroup>
                <col className="w-[15%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[38%]" />
                <col className="w-[7%]" />
              </colgroup>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-muted/30 hover:bg-transparent"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={cn(
                          "h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                          (header.id === "runningBalance" ||
                            header.id === "companyBalance") &&
                            "text-right",
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-32 text-center"
                    >
                      <span className="text-sm text-muted-foreground">
                        No entries found.
                      </span>
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => {
                    const isSelected = selectedEntryId === row.original._id;
                    return (
                      <TableRow
                        key={row.id}
                        className={cn(
                          "cursor-pointer transition-colors",
                          isSelected
                            ? "hover:bg-primary/8 bg-primary/5"
                            : "hover:bg-muted/50",
                        )}
                        onClick={() => setSelectedEntryId(row.original._id)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              "px-4 py-3",
                              (cell.column.id === "runningBalance" ||
                                cell.column.id === "companyBalance") &&
                                "text-right",
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}

                {/* Total row */}
                <TableRow className="border-t-2 bg-muted/20 hover:bg-muted/20">
                  <TableCell className="px-4 py-3 text-sm font-bold text-muted-foreground">
                    TOTAL
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right">
                    <span className="text-sm font-bold tabular-nums text-rose-600 dark:text-rose-400">
                      {fmt(totalRunning)}
                    </span>
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-5 py-3">
          <p className="text-center text-xs text-muted-foreground">
            Click any row to view flags, balances, source documents, and edit
          </p>
        </div>
      </div>

      {/* Rebuilt RBA ledger detail(s) — full-width chart, debt breakdown, and
          expandable cleaned movements for any authority with a rebuilt ledger. */}
      {entries
        .filter((e) => e.ledger)
        .map((e) => (
          <LedgerDetailCard key={e._id} entry={e} />
        ))}

      {/* Detail panel */}
      <AtoSroDetailSheet
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        onDeleted={() => {
          setSelectedEntryId(null);
          linkedStack.closeAll();
        }}
        isViewer={isViewer}
        flagCtx={flagCtx}
        onOpenLinked={linkedStack.open}
      />

      {/* Stack of side-by-side linked records */}
      <SecondaryDetailPanel
        jobId={jobId}
        stack={linkedStack.stack}
        onCloseAt={linkedStack.closeAt}
        onOpenLinked={linkedStack.open}
        isViewer={isViewer}
      />

      {/* Add account form */}
      {showAddForm && (
        <AddAccountSheet
          jobId={jobId}
          taskId={taskId}
          open={showAddForm}
          onClose={() => setShowAddForm(false)}
          onCreated={(id) => {
            setShowAddForm(false);
            setSelectedEntryId(id);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail Sheet (slide-over)
// ---------------------------------------------------------------------------

export function AtoSroDetailSheet({
  entry,
  open,
  onClose,
  onDeleted,
  isViewer,
  flagCtx,
  placement = "primary",
  stackIndex,
  onOpenLinked,
}: {
  entry: AtoSroEntryUI | null;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
  isViewer?: boolean;
  flagCtx: UseFlagDismissalsResult;
  placement?: "primary" | "secondary";
  /** Position in the secondary panel stack (1 = closest to primary). */
  stackIndex?: number;
  onOpenLinked?: (type: RecordType, id: string) => void;
}) {
  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      placement={placement}
      stackIndex={stackIndex}
    >
      {entry && (
        <AtoSroDetailContent
          entry={entry}
          isViewer={isViewer}
          onDeleted={onDeleted}
          flagCtx={flagCtx}
          onOpenLinked={onOpenLinked}
        />
      )}
    </SlideOverPanel>
  );
}

function AtoSroDetailContent({
  entry,
  isViewer,
  onDeleted,
  flagCtx,
  onOpenLinked,
}: {
  entry: AtoSroEntryUI;
  isViewer?: boolean;
  onDeleted: () => void;
  flagCtx: UseFlagDismissalsResult;
  onOpenLinked?: (type: RecordType, id: string) => void;
}) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  const updateBalance = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.atoSro.publicMutations.updateBalance,
    ),
  });
  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.atoSro.publicMutations.updateAtoSroField,
    ),
  });
  const deleteEntry = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.atoSro.publicMutations.deleteAtoSroEntry,
    ),
  });
  const setBalanceSelection = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.atoSro.publicMutations.setBalanceSelection,
    ),
  });
  const clearBalanceSelection = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.atoSro.publicMutations
        .clearBalanceSelection,
    ),
  });
  const [showManualBalance, setShowManualBalance] = useState(false);
  const [manualAmount, setManualAmount] = useState("");
  const [manualReason, setManualReason] = useState("");

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!entry.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of entry.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [entry.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  const canEdit = !isViewer;

  function startEdit(field: string, currentValue: string) {
    if (!canEdit) return;
    setEditingField(field);
    setEditDraft(currentValue);
  }

  // Stages the edit behind the shared confirmation dialog — the mutation only
  // fires once the practitioner confirms (optionally leaving a note).
  function saveEdit(field: string) {
    if (field === "runningBalance" || field === "companyBalance") {
      const amount = parseFloat(editDraft);
      if (!isNaN(amount)) {
        setPendingEdit({
          fieldLabel:
            field === "runningBalance" ? "Running balance" : "Company balance",
          onConfirm: (note) =>
            updateBalance.mutateAsync({
              entryId: entry._id as Id<"atoSroEntries">,
              balanceType: field,
              amount,
              note,
            }),
        });
      }
    } else if (field === "notes") {
      const value = editDraft;
      setPendingEdit({
        fieldLabel: ATO_SRO_FIELD_LABELS.notes,
        onConfirm: (note) =>
          updateField.mutateAsync({
            entryId: entry._id as Id<"atoSroEntries">,
            field: "notes",
            value,
            note,
          }),
      });
    }
    setEditingField(null);
  }

  function cancelEdit() {
    setEditingField(null);
    setEditDraft("");
  }

  const variance =
    entry.runningBalance?.amount != null && entry.companyBalance?.amount != null
      ? Math.abs(entry.runningBalance.amount - entry.companyBalance.amount)
      : null;

  // Merged list of every reported balance figure for this authority — the
  // rebuilt-ledger observation plus the statement/disclosure running balance
  // and company GL — deduped by amount so the practitioner sees all the
  // "ties" in one place (MOR-587).
  const displaySources: Array<{
    key: string;
    sourceType: string;
    label: string;
    amount: number;
    asAtDate?: string;
    documentId?: string;
  }> = [];
  {
    // Resolve a source string (often a filename) back to its source document id
    // so the row can link to the original.
    const resolveDocId = (src?: string): string | undefined => {
      if (!src) return undefined;
      const exact = entry.sourceDocuments.find((d) => d.fileName === src);
      if (exact) return exact.documentId;
      const partial = entry.sourceDocuments.find(
        (d) => src.includes(d.fileName) || d.fileName.includes(src),
      );
      return partial?.documentId;
    };
    const seen = new Set<number>();
    const add = (s: {
      sourceType: string;
      label: string;
      amount: number;
      asAtDate?: string;
      documentId?: string;
    }) => {
      const k = Math.round(s.amount);
      if (seen.has(k)) return;
      seen.add(k);
      displaySources.push({ ...s, key: `${s.sourceType}-${k}` });
    };
    for (const o of entry.balanceObservations ?? []) {
      add({
        sourceType: o.sourceType,
        label: o.label ?? BALANCE_SOURCE_LABELS[o.sourceType] ?? o.sourceType,
        amount: o.amount,
        asAtDate: o.asAtDate,
        documentId: o.documentId ?? resolveDocId(o.label),
      });
    }
    if (entry.runningBalance) {
      add({
        sourceType: "ato_statement",
        label: entry.runningBalance.source || "Statement balance",
        amount: entry.runningBalance.amount,
        asAtDate: entry.runningBalance.asAtDate,
        documentId: resolveDocId(entry.runningBalance.source),
      });
    }
    if (entry.companyBalance) {
      add({
        sourceType: "director_gl",
        label: entry.companyBalance.source || "Company GL",
        amount: entry.companyBalance.amount,
        asAtDate: entry.companyBalance.asAtDate,
        documentId: resolveDocId(entry.companyBalance.source),
      });
    }
    displaySources.sort((a, b) => b.amount - a.amount);
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="px-6 pb-4 pt-6">
        <div className="space-y-1">
          <div className="mb-1 flex items-center gap-2">
            <Badge
              color={getAuthorityBadgeColor(entry.authority)}
              variant="soft"
              size="sm"
              className="font-bold tracking-wide"
            >
              {getAuthorityShortLabel(entry.authority)}
            </Badge>
          </div>
          <h2 className="text-base font-semibold leading-tight">
            {entry.authorityLabel}
          </h2>
        </div>
      </div>

      {/* Running Balance Banner */}
      <div className="border-y border-rose-200/30 bg-rose-50/50 px-6 py-3 dark:border-rose-800/30 dark:bg-rose-950/20">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-400">
          Running Balance
        </p>
        {entry.selectedBalance ? (
          <>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-foreground">
                {fmt(entry.selectedBalance.amount)}
              </span>
              <Badge
                color="blue"
                variant="soft"
                size="sm"
                className="text-[10px]"
              >
                {entry.selectedBalance.source === "manual"
                  ? "Manual override"
                  : `Selected: ${entry.selectedBalance.label ?? entry.selectedBalance.source}`}
              </Badge>
            </div>
            <p className="mt-0.5 text-[10px] text-rose-700/60 dark:text-rose-400/60">
              Chosen by {entry.selectedBalance.setByName ?? "practitioner"}
              {entry.selectedBalance.reason
                ? ` — ${entry.selectedBalance.reason}`
                : ""}
              . Change it under Balance sources below.
            </p>
          </>
        ) : entry.ledger ? (
          <>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-2xl font-bold tabular-nums text-foreground">
                {fmt(entry.ledger.closingBalance)}
              </span>
              <Badge variant="secondary" size="sm" className="text-[10px]">
                {entry.ledgers && entry.ledgers.length > 1
                  ? `ATO ${entry.ledgers
                      .map((l) => l.accountType.toUpperCase())
                      .sort()
                      .join(" + ")} · rebuilt`
                  : "ATO RBA · rebuilt"}
              </Badge>
            </div>
            <p className="mt-0.5 text-[10px] text-rose-700/60 dark:text-rose-400/60">
              Rebuilt from {entry.ledger.rowCount} ATO ledger movements
              {entry.ledger.asAtDate ? ` · as at ${entry.ledger.asAtDate}` : ""}
              . Other reported figures are listed under Balance sources below.
            </p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-3">
              {editingField === "runningBalance" ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit("runningBalance");
                      if (e.key === "Escape") cancelEdit();
                    }}
                    className="h-9 w-40 text-lg font-bold tabular-nums"
                    autoFocus
                  />
                  <Button size="sm" onClick={() => saveEdit("runningBalance")}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <span
                  className={cn(
                    "text-2xl font-bold tabular-nums text-foreground",
                    canEdit &&
                      "group cursor-pointer transition-colors hover:text-rose-600 dark:hover:text-rose-400",
                  )}
                  onClick={() =>
                    startEdit(
                      "runningBalance",
                      String(entry.runningBalance?.amount ?? ""),
                    )
                  }
                >
                  {fmt(entry.runningBalance?.amount)}
                  {canEdit && (
                    <Pencil className="ml-2 inline-block h-3 w-3 opacity-0 group-hover:opacity-50" />
                  )}
                </span>
              )}
            </div>
            {entry.runningBalance?.source && (
              <p className="mt-0.5 text-[10px] text-rose-700/60 dark:text-rose-400/60">
                {entry.runningBalance.source}
              </p>
            )}
          </>
        )}
      </div>

      <Separator />

      {/* Scrollable body */}
      <ScrollArea className="min-h-0 flex-1">
        {/* width:0 + min-width:100% clamps the Radix ScrollArea's
            `display:table` viewport to the panel width — otherwise wide content
            (long balance-source labels, flag text) drives it to its max-content
            width and the panel's overflow-x-hidden clips the right edge. */}
        <div
          className="space-y-6 px-6 py-5"
          style={{ width: 0, minWidth: "100%" }}
        >
          {/* Company Balance */}
          <PanelSection
            title="Company Balance"
            action={
              <InfoHint text="What the company's own books say it owes this authority (from director disclosures / ROCAP / general ledger) — as distinct from the authority's figure above. A gap between the two is itself a flag (non-matching balances): unlodged returns, interest the company hasn't accrued, or under-declared debt." />
            }
          >
            {editingField === "companyBalance" ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveEdit("companyBalance");
                    if (e.key === "Escape") cancelEdit();
                  }}
                  className="h-9 w-40 text-sm font-semibold tabular-nums"
                  autoFocus
                />
                <Button size="sm" onClick={() => saveEdit("companyBalance")}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div>
                <span
                  className={cn(
                    "text-sm font-bold tabular-nums",
                    entry.companyBalance?.amount != null
                      ? "text-foreground"
                      : "text-muted-foreground/50",
                    canEdit &&
                      "group cursor-pointer transition-colors hover:text-primary",
                  )}
                  onClick={() =>
                    startEdit(
                      "companyBalance",
                      String(entry.companyBalance?.amount ?? ""),
                    )
                  }
                >
                  {fmt(entry.companyBalance?.amount)}
                  {canEdit && (
                    <Pencil className="ml-1 inline-block h-2.5 w-2.5 opacity-0 group-hover:opacity-50" />
                  )}
                </span>
                {entry.companyBalance?.source && (
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {entry.companyBalance.source}
                  </p>
                )}
              </div>
            )}

            {/* Variance callout */}
            {variance != null && variance > 0 && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/20">
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                  Variance
                </span>
                <span className="text-sm font-bold tabular-nums text-amber-700 dark:text-amber-400">
                  {fmt(variance)}
                </span>
              </div>
            )}
          </PanelSection>

          {/* Balance sources — multi-source "ties" + practitioner override (MOR-587) */}
          {displaySources.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Balance sources
                </h4>
                <InfoHint text="The same liability as reported by each source. By default the rebuilt ATO RBA is used as the running balance; click a source to use it instead, or enter a manual override." />
              </div>

              {/* Active manual override (not one of the listed sources) */}
              {entry.selectedBalance?.source === "manual" && (
                <div className="flex items-center justify-between gap-2 rounded-lg border-2 border-blue-200 bg-blue-50/40 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/10">
                  <div className="flex min-w-0 items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                    <div className="flex min-w-0 flex-col">
                      <span className="text-xs font-medium">
                        Manual override
                      </span>
                      {entry.selectedBalance.reason && (
                        <span className="truncate text-[10px] text-muted-foreground">
                          {entry.selectedBalance.reason}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {fmt(entry.selectedBalance.amount)}
                  </span>
                </div>
              )}

              <div className="divide-y divide-border rounded-lg border">
                {displaySources.map((s) => {
                  const active =
                    entry.selectedBalance?.source !== "manual" &&
                    Math.round(s.amount) ===
                      Math.round(getEffectiveBalance(entry) ?? NaN);
                  return (
                    <div
                      key={s.key}
                      className={cn(
                        "flex items-center gap-2 px-2.5 py-2",
                        active && "bg-emerald-50/40 dark:bg-emerald-950/10",
                      )}
                    >
                      {canEdit ? (
                        <button
                          type="button"
                          onClick={() =>
                            setBalanceSelection.mutate({
                              entryId: entry._id as Id<"atoSroEntries">,
                              source: s.sourceType,
                              label: s.label,
                              amount: s.amount,
                              asAtDate: s.asAtDate,
                            })
                          }
                          className="shrink-0"
                          aria-label="Use this balance"
                        >
                          {active ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          ) : (
                            <Circle className="h-4 w-4 text-muted-foreground/40 transition-colors hover:text-muted-foreground" />
                          )}
                        </button>
                      ) : active ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <Circle className="h-4 w-4 shrink-0 text-muted-foreground/30" />
                      )}
                      <div className="flex min-w-0 flex-1 flex-col">
                        {s.documentId ? (
                          <button
                            type="button"
                            onClick={() =>
                              setViewingDocumentId(
                                s.documentId as Id<"documents">,
                              )
                            }
                            className="group min-w-0 text-left"
                          >
                            <span className="flex items-center gap-1.5 text-xs font-medium">
                              <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                              <span className="truncate underline-offset-2 group-hover:underline">
                                {s.label}
                              </span>
                              {s.sourceType === "rba_ledger" && (
                                <Badge variant="secondary" size="sm">
                                  authoritative
                                </Badge>
                              )}
                            </span>
                          </button>
                        ) : (
                          <span className="flex items-center gap-1.5 text-xs font-medium">
                            <span className="truncate">{s.label}</span>
                            {s.sourceType === "rba_ledger" && (
                              <Badge variant="secondary" size="sm">
                                authoritative
                              </Badge>
                            )}
                          </span>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {s.asAtDate ? `as at ${s.asAtDate}` : ""}
                          {active
                            ? `${s.asAtDate ? " · " : ""}in use${entry.selectedBalance ? "" : " (auto)"}`
                            : ""}
                        </span>
                      </div>
                      <span className="shrink-0 text-sm font-semibold tabular-nums">
                        {fmt(s.amount)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Manual override + revert controls */}
              {canEdit && (
                <div className="space-y-2">
                  {showManualBalance ? (
                    <div className="flex flex-col gap-1.5 rounded-lg border border-border p-2.5">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          placeholder="Amount"
                          value={manualAmount}
                          onChange={(e) => setManualAmount(e.target.value)}
                          className="h-8 w-32 text-sm tabular-nums"
                          autoFocus
                        />
                        <Input
                          placeholder="Reason (optional)"
                          value={manualReason}
                          onChange={(e) => setManualReason(e.target.value)}
                          className="h-8 flex-1 text-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            const amt = parseFloat(manualAmount);
                            if (!isNaN(amt)) {
                              setBalanceSelection.mutate({
                                entryId: entry._id as Id<"atoSroEntries">,
                                source: "manual",
                                label: "Manual override",
                                amount: amt,
                                reason: manualReason || undefined,
                              });
                              setShowManualBalance(false);
                              setManualReason("");
                            }
                          }}
                        >
                          Save override
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowManualBalance(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => {
                          setShowManualBalance(true);
                          setManualAmount(
                            String(Math.round(getEffectiveBalance(entry) ?? 0)),
                          );
                        }}
                        className="text-[11px] text-primary hover:underline"
                      >
                        + Manual override
                      </button>
                      {entry.selectedBalance && (
                        <button
                          type="button"
                          onClick={() =>
                            clearBalanceSelection.mutate({
                              entryId: entry._id as Id<"atoSroEntries">,
                            })
                          }
                          className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                        >
                          Revert to automatic
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {entry.ledger && entry.ledger.reconciledToPod !== "no_pod" && (
                <div
                  className={cn(
                    "flex items-center justify-between rounded-md px-3 py-1.5 text-xs",
                    entry.ledger.reconciledToPod === "matched"
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400"
                      : "bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400",
                  )}
                >
                  <span className="font-medium">
                    {entry.ledger.reconciledToPod === "matched"
                      ? "Rebuilt closing reconciles to Proof of Debt"
                      : "Rebuilt closing differs from Proof of Debt"}
                  </span>
                  {entry.ledger.reconciledToPod === "variance" &&
                    entry.ledger.podVariance != null && (
                      <span className="font-bold tabular-nums">
                        {fmt(entry.ledger.podVariance)}
                      </span>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Flags */}
          {entry.flags.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Flags
                </h4>
                <Badge variant="secondary" size="sm">
                  {entry.flags.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {entry.flags.map((flag) => {
                  const severity = FLAG_SEVERITY[flag.type] ?? "info";
                  const flagDetail = atoFlagDetail(flag.details);
                  const flagLabel = FLAG_TYPE_LABELS[flag.type] ?? flag.type;
                  const dismissed = flagCtx.isDismissed("atoSro", entry._id, {
                    type: flag.type,
                    detail: flagDetail,
                  });
                  return (
                    <div key={flag.type}>
                      <Collapsible defaultOpen={flag.details.length <= 3}>
                        <div
                          className={cn(
                            "overflow-hidden rounded-lg border",
                            severity === "critical"
                              ? "border-rose-200 bg-rose-50/50 dark:border-rose-800 dark:bg-rose-950/20"
                              : severity === "warning"
                                ? "border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20"
                                : "border-sky-200 bg-sky-50/50 dark:border-sky-800 dark:bg-sky-950/20",
                            dismissed && "opacity-50",
                          )}
                        >
                          <div className="flex items-center">
                            <CollapsibleTrigger className="group flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left">
                              <AlertTriangle
                                className={cn(
                                  "h-4 w-4 shrink-0",
                                  severity === "critical"
                                    ? "text-rose-500"
                                    : severity === "warning"
                                      ? "text-amber-500"
                                      : "text-sky-500",
                                )}
                              />
                              <span
                                className={cn(
                                  "flex-1 text-xs font-semibold uppercase tracking-wider",
                                  severity === "critical"
                                    ? "text-rose-700 dark:text-rose-400"
                                    : severity === "warning"
                                      ? "text-amber-700 dark:text-amber-400"
                                      : "text-sky-700 dark:text-sky-400",
                                )}
                              >
                                {FLAG_TYPE_LABELS[flag.type] ?? flag.type}
                                <span className="ml-1.5 text-[10px] font-normal opacity-70">
                                  ({flag.details.length})
                                </span>
                              </span>
                              <ChevronDown
                                className={cn(
                                  "h-3.5 w-3.5 shrink-0 transition-transform",
                                  severity === "critical"
                                    ? "text-rose-400 dark:text-rose-600"
                                    : severity === "warning"
                                      ? "text-amber-400 dark:text-amber-600"
                                      : "text-sky-400 dark:text-sky-600",
                                  "group-data-[state=open]:rotate-180",
                                )}
                              />
                            </CollapsibleTrigger>
                            <div className="shrink-0 pr-2">
                              <FlagDismissButton
                                ctx={flagCtx}
                                agentKind="atoSro"
                                entryId={entry._id}
                                flagType={flag.type}
                                flagDetail={flagDetail}
                              />
                            </div>
                          </div>
                          <CollapsibleContent>
                            <div className="space-y-2 px-3 pb-2.5">
                              {flag.details.map((detail, di) => (
                                <div
                                  key={di}
                                  className={cn(
                                    "ml-[5px] border-l-2 pl-4 text-sm text-muted-foreground",
                                    severity === "critical"
                                      ? "border-rose-200 dark:border-rose-800"
                                      : severity === "warning"
                                        ? "border-amber-200 dark:border-amber-800"
                                        : "border-sky-200 dark:border-sky-800",
                                  )}
                                >
                                  {detail}
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                      <FlagDismissEditor
                        ctx={flagCtx}
                        agentKind="atoSro"
                        entryId={entry._id}
                        entryLabel={entry.authorityLabel}
                        flagType={flag.type}
                        flagLabel={flagLabel}
                        flagDetail={flagDetail}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <AtoSroNotesSection
            notes={entry.notes}
            canEdit={canEdit}
            editing={editingField === "notes"}
            editDraft={editDraft}
            onStartEdit={() => startEdit("notes", entry.notes)}
            onDraftChange={setEditDraft}
            onSave={() => saveEdit("notes")}
            onCancel={cancelEdit}
          />

          {/* Source Documents */}
          {entry.sourceDocuments.length > 0 && (
            <PanelSection
              title="Source Documents"
              count={entry.sourceDocuments.length}
              collapsible
            >
              <div className="space-y-1.5">
                {entry.sourceDocuments.map((doc, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() =>
                      setViewingDocumentId(doc.documentId as Id<"documents">)
                    }
                    className="group flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                  >
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground">
                        {doc.fileName}
                      </span>
                      {doc.detail && (
                        <span className="text-[10px] text-muted-foreground">
                          {doc.detail}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                  </button>
                ))}
              </div>
            </PanelSection>
          )}

          {/* Suggested links */}
          {onOpenLinked && (
            <SuggestedLinksSection
              jobId={entry.jobId}
              anchor={{ type: "ato_sro", id: entry._id }}
              onOpenLinked={onOpenLinked}
              isViewer={isViewer}
            />
          )}

          {/* Linked records */}
          {onOpenLinked && (
            <LinkedRecordsSection
              jobId={entry.jobId}
              anchor={{ type: "ato_sro", id: entry._id }}
              onOpenLinked={onOpenLinked}
              isViewer={isViewer}
            />
          )}

          {/* History */}
          <ChangeHistory
            history={entry.changeHistory}
            usersById={historyUsersById}
            formatters={ATO_SRO_FIELD_FORMATTERS}
            fieldLabels={ATO_SRO_FIELD_LABELS}
          />

          {/* Delete */}
          {canEdit && (
            <div className="border-t pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={async () => {
                  await deleteEntry.mutateAsync({
                    entryId: entry._id as Id<"atoSroEntries">,
                  });
                  onDeleted();
                }}
                disabled={deleteEntry.isPending}
              >
                {deleteEntry.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                Delete Account
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Confirmation gate for every inline edit in this panel */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => setPendingEdit(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Account Sheet
// ---------------------------------------------------------------------------

const AUTHORITY_OPTIONS = [
  { value: "ato", label: "ATO — Australian Taxation Office" },
  { value: "sro_vic", label: "SRO — State Revenue Office (VIC)" },
  { value: "sro_nsw", label: "SRO — Revenue NSW" },
  { value: "sro_qld", label: "SRO — Queensland Revenue Office" },
  { value: "sro_wa", label: "SRO — Revenue Western Australia" },
  { value: "sro_sa", label: "SRO — Revenue SA" },
  { value: "sro_tas", label: "SRO — State Revenue Office (TAS)" },
  { value: "sro_act", label: "SRO — ACT Revenue Office" },
  { value: "sro_nt", label: "SRO — Territory Revenue Office (NT)" },
  { value: "other", label: "Other Tax Authority" },
] as const;

type AuthorityOption = (typeof AUTHORITY_OPTIONS)[number]["value"];

function isAuthorityOption(value: string): value is AuthorityOption {
  return AUTHORITY_OPTIONS.some((o) => o.value === value);
}

function getAuthorityLabel(value: string): string {
  const opt = AUTHORITY_OPTIONS.find((o) => o.value === value);
  return opt ? (opt.label.split(" — ")[1] ?? opt.label) : value;
}

function AddAccountSheet({
  jobId,
  taskId,
  open,
  onClose,
  onCreated,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [authority, setAuthority] = useState<AuthorityOption>("ato");
  const [runningBalance, setRunningBalance] = useState("");
  const [companyBalance, setCompanyBalance] = useState("");
  const [notes, setNotes] = useState("");

  const addEntry = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.atoSro.publicMutations.addManualEntry,
    ),
  });

  async function handleSubmit() {
    const result = await addEntry.mutateAsync({
      jobId,
      taskId,
      authority,
      authorityLabel: getAuthorityLabel(authority),
      runningBalance: runningBalance ? parseFloat(runningBalance) : undefined,
      companyBalance: companyBalance ? parseFloat(companyBalance) : undefined,
      notes: notes.trim() || undefined,
    });
    onCreated(result as unknown as string);
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-md">
        <div className="px-6 pb-4 pt-6">
          <SheetHeader>
            <SheetTitle className="text-lg font-bold">
              Add New Account
            </SheetTitle>
            <SheetDescription>
              Manually add an ATO or SRO account to the summary.
            </SheetDescription>
          </SheetHeader>
        </div>

        <Separator />

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-5 px-6 py-5">
            {/* Authority */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Authority
              </label>
              <Select
                value={authority}
                onValueChange={(value) => {
                  if (isAuthorityOption(value)) setAuthority(value);
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Running Balance */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Running Balance
              </label>
              <Input
                type="number"
                value={runningBalance}
                onChange={(e) => setRunningBalance(e.target.value)}
                placeholder="e.g. 312450"
                className="h-9 text-sm tabular-nums"
              />
            </div>

            {/* Company Balance */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Company Balance
              </label>
              <Input
                type="number"
                value={companyBalance}
                onChange={(e) => setCompanyBalance(e.target.value)}
                placeholder="e.g. 308720"
                className="h-9 text-sm tabular-nums"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional context..."
                className="min-h-[80px] text-sm"
              />
            </div>
          </div>
        </ScrollArea>

        <Separator />

        <div className="flex justify-end gap-2 px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={addEntry.isPending}
            className="gap-1.5"
          >
            {addEntry.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            Add Account
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Collapsible markdown notes (mirrors LoanDetailPanel / AssetPanel)
// ---------------------------------------------------------------------------

function AtoSroNotesSection({
  notes,
  canEdit,
  editing,
  editDraft,
  onStartEdit,
  onDraftChange,
  onSave,
  onCancel,
}: {
  notes: string;
  canEdit: boolean;
  editing: boolean;
  editDraft: string;
  onStartEdit: () => void;
  onDraftChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const normalisedNotes = notes ? normaliseNotesForMarkdown(notes) : "";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
          Notes
        </h4>
        {canEdit && !editing && (
          <button
            onClick={onStartEdit}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <Textarea
            value={editDraft}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="Add notes (supports **bold** and - bullet points)..."
            className="min-h-[160px] resize-y p-3 text-sm leading-relaxed"
            autoFocus
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={onSave}
              className="rounded bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Save
            </button>
            <button
              onClick={onCancel}
              className="rounded px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div
            className={cn(
              "prose prose-sm relative max-w-none overflow-hidden py-2 transition-[max-height] duration-200 dark:prose-invert [&_li]:text-sm [&_li]:leading-relaxed [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-foreground [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:pl-4",
              !expanded && "max-h-[180px]",
            )}
          >
            {normalisedNotes ? (
              <ReactMarkdown>{normalisedNotes}</ReactMarkdown>
            ) : (
              <p className="text-muted-foreground">No notes recorded.</p>
            )}
            {normalisedNotes && !expanded && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
            )}
          </div>
          {normalisedNotes && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-2 rounded-md border border-primary/30 px-3 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel Section (matches AssetPanel pattern)
// ---------------------------------------------------------------------------

function PanelSection({
  title,
  count,
  action,
  children,
  collapsible = false,
  defaultOpen = true,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  const titleAndCount = (
    <div className="flex items-center gap-2">
      <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
        {title}
      </h4>
      {count != null && (
        <Badge variant="secondary" size="sm">
          {count}
        </Badge>
      )}
    </div>
  );

  if (collapsible) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="group flex w-full items-center justify-between gap-2 text-left"
          aria-expanded={open}
        >
          {titleAndCount}
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
        {open && children}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {titleAndCount}
        {action}
      </div>
      {children}
    </div>
  );
}
